import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import Swal from "sweetalert2";
import SessionHelper from "../Helper/SessionHelper";
import {
  deleteAuthorCaptionAlias,
  fetchAuthorCaptionAliases,
  saveAuthorCaptionAlias,
} from "../../actions/captionsActions";

/**
 * Per-author caption-alias section, rendered inside AddEditAuthor.
 * Hidden if the operator doesn't have manage_captions — the rest of
 * the Author page works fine without it.
 *
 * For new (unsaved) authors there's no author_id yet, so the section
 * renders a hint asking the operator to save the author first. Once
 * saved the section becomes fully interactive.
 */
const AuthorCaptionAliases = ({ authorId }) => {
  const dispatch = useDispatch();
  const aliasesByAuthor = useSelector(s => s.captions.aliasesByAuthor);
  const aliases = authorId ? (aliasesByAuthor[authorId] ?? []) : [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ misheard: "", canonical: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const hasPermission = SessionHelper.hasPermission(["manage_captions"]);

  useEffect(() => {
    if (authorId && hasPermission) {
      dispatch(fetchAuthorCaptionAliases(authorId));
    }
  }, [dispatch, authorId, hasPermission]);

  if (!hasPermission) {
    return null;
  }

  const openCreate = () => {
    setDraft({ misheard: "", canonical: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = alias => {
    setDraft({ ...alias });
    setDialogOpen(true);
  };

  const onSave = () => {
    // Guard against rapid double-clicks racing the dialog open state.
    if (submitting) return;
    if (!draft.misheard.trim() || !draft.canonical.trim()) {
      Swal.fire({ icon: "warning", title: "Both misheard and canonical are required.", timer: 1500 });
      return;
    }
    setSubmitting(true);
    dispatch(
      saveAuthorCaptionAlias(
        authorId,
        {
          ...draft,
          misheard: draft.misheard.trim(),
          canonical: draft.canonical.trim(),
          notes: draft.notes?.trim() || null,
        },
        () => setDialogOpen(false)
      )
    ).finally(() => setSubmitting(false));
  };

  const onDelete = async alias => {
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      // titleText (not title) — alias.misheard/canonical come from user-controlled
      // DB rows; Swal's `title` renders as HTML and would execute injected markup.
      titleText: `Remove "${alias.misheard}" → "${alias.canonical}"?`,
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    dispatch(deleteAuthorCaptionAlias(authorId, alias.id));
  };

  if (!authorId) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          <strong>Caption aliases</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Save the author first to add per-author caption corrections (e.g. <em>"Wertner" → "Ortner"</em>).
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="subtitle1">
            <strong>Caption aliases</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mishearings the post-processor swaps for the canonical form on this author's sessions.
          </Typography>
        </Box>
        <Button startIcon={<Add />} variant="outlined" size="small" onClick={openCreate}>
          Add alias
        </Button>
      </Stack>

      {aliases.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No aliases configured for this author yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Misheard</strong>
                </TableCell>
                <TableCell>
                  <strong>Canonical</strong>
                </TableCell>
                <TableCell>
                  <strong>Notes</strong>
                </TableCell>
                <TableCell align="right" sx={{ width: 96 }}>
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aliases.map(alias => (
                <TableRow key={alias.id} hover>
                  <TableCell>{alias.misheard}</TableCell>
                  <TableCell>{alias.canonical}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" color="text.secondary" noWrap title={alias.notes ?? ""}>
                      {alias.notes || ""}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(alias)}
                        aria-label={`Edit alias ${alias.misheard}`}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={() => onDelete(alias)}
                        color="error"
                        aria-label={`Remove alias ${alias.misheard}`}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft.id ? "Edit alias" : "Add alias"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Misheard"
              helperText="What the transcriber hears (e.g. Wertner)."
              value={draft.misheard}
              onChange={e => setDraft({ ...draft, misheard: e.target.value })}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Canonical"
              helperText="What should appear in captions (e.g. Ortner)."
              value={draft.canonical}
              onChange={e => setDraft({ ...draft, canonical: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Notes (optional)"
              value={draft.notes ?? ""}
              onChange={e => setDraft({ ...draft, notes: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={onSave} variant="contained" disabled={submitting}>
            {draft.id ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AuthorCaptionAliases;
